import {
  Modal as NextUIModal,
  type ModalProps as NextUIModalProps,
  ModalBody as NextUIModalBody,
  type ModalBodyProps as NextUIModalBodyProps,
  ModalContent as NextUIModalContent,
  type ModalContentProps as NextUIModalContentProps,
  ModalFooter as NextUIModalFooter,
  type ModalFooterProps as NextUIModalFooterProps,
  ModalHeader as NextUIModalHeader,
  type ModalHeaderProps as NextUIModalHeaderProps,
} from '@heroui/modal';
import { type SlotsToClasses, forwardRef } from '@heroui/react';
import { type ComponentProps, type ReactNode, type Ref, createContext, useContext, useEffect, useState } from 'react';
import { XIcon } from '@phosphor-icons/react';
import useIsEmbed from '@embed/useIsEmbed';
import usePresentationScale from '@embed/usePresentationScale';
import {
  Modal as SimpleModal,
  ModalContent as SimpleModalContent,
  ModalHeader as SimpleModalHeader,
  ModalBody as SimpleModalBody,
  ModalFooter as SimpleModalFooter,
} from './simple-modal';

// The simple-modal has no React context for its close handler, so we bridge the
// HeroUI `ModalContent` render-prop pattern (`{(onClose) => ...}`) ourselves.
const SimpleModalCloseContext = createContext<() => void>(() => {});

interface ModalProps extends NextUIModalProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  disableOuterScroll?: false | true;
  theme?: string;
  customClassNames?: SlotsToClasses<'backdrop' | 'base' | 'wrapper' | 'header' | 'body' | 'footer' | 'closeButton'> | undefined,
  width?: string;
  height?: string;
}

export const Modal = forwardRef(
  ({
    size = 'md',
    placement = 'top',
    disableOuterScroll,
    theme = '',
    customClassNames,
    width,
    height = '',
    isOpen,
    scrollBehavior,
    ...props
  }: ModalProps, ref) => {
    const isEmbed = useIsEmbed();
    const presentationScale = usePresentationScale(Boolean(isOpen));

    if (isEmbed) {
      const handleClose = () => {
        props.onClose?.();
        props.onOpenChange?.(false);
      };

      return (
        <SimpleModal
          isOpen={Boolean(isOpen)}
          onClose={props.onClose}
          onOpenChange={props.onOpenChange}
          hideCloseButton={props.hideCloseButton}
          isDismissable={props.isDismissable}
          size={size}
          placement={placement}
          scale={presentationScale}
          width={width}
          height={height}
          scrollBehavior={scrollBehavior === 'inside' ? 'inside' : 'outside'}
          theme={theme}
          className={props.className}
          classNames={customClassNames as ComponentProps<typeof SimpleModal>['classNames']}
        >
          <SimpleModalCloseContext.Provider value={handleClose}>
            {props.children}
          </SimpleModalCloseContext.Provider>
        </SimpleModal>
      );
    }

    const sizes = {
      sm: 'w-110',
      md: 'w-150',
      lg: 'w-200',
      xl: 'w-300',
      full: 'w-full',
    }[size];

    return (
      <NextUIModal
        classNames={customClassNames || {
          wrapper: `${disableOuterScroll && 'overflow-y-hidden'}`,
          backdrop: 'bg-black/75',
          base: `${theme} shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-lg p-7.5 rounded-xl max-w-full !bg-content2 text-content2-foreground ${sizes} ${height} ${width}`,
          header: 'p-0 text-xl',
          body: 'p-0 pt-2 gap-0',
          footer: 'p-0 pt-4',
          closeButton:
            'p-3 hover:bg-transparent text-content2-foreground rounded-none active:bg-transparent',
        }}

        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.2,
                ease: 'easeOut',
              },
            },
            exit: {
              y: 20,
              opacity: 0,
              transition: {
                duration: 0.1,
                ease: 'easeIn',
              },
            },
          },
        }}
        closeButton={
          <div>
            <XIcon className='cursor-pointer text-default-500 hover:text-default-900' size={24} />
          </div>
        }
        placement={placement}
        scrollBehavior={scrollBehavior}
        isOpen={isOpen}
        ref={ref}
        {...props}
      />
    );
  }
);

interface ModalBodyProps extends NextUIModalBodyProps { }

export const ModalBody = forwardRef((props: ModalBodyProps, ref) => {
  const isEmbed = useIsEmbed();
  if (isEmbed) {
    return <SimpleModalBody ref={ref as Ref<HTMLDivElement>} className={props.className}>{props.children}</SimpleModalBody>;
  }
  return <NextUIModalBody ref={ref} {...props} />;
});

interface ModalContentProps extends NextUIModalContentProps { }

export const ModalContent = forwardRef((props: ModalContentProps, ref) => {
  const isEmbed = useIsEmbed();
  const close = useContext(SimpleModalCloseContext);
  if (isEmbed) {
    const { children, className } = props;
    const resolved = typeof children === 'function'
      ? (children as (onClose: () => void) => ReactNode)(close)
      : children;
    return <SimpleModalContent className={className}>{resolved}</SimpleModalContent>;
  }
  return <NextUIModalContent {...props} />;
});

interface ModalFooterProps extends NextUIModalFooterProps { }

export const ModalFooter = forwardRef((props: ModalFooterProps, ref) => {
  const isEmbed = useIsEmbed();
  if (isEmbed) {
    return <SimpleModalFooter className={props.className}>{props.children}</SimpleModalFooter>;
  }
  return <NextUIModalFooter ref={ref} {...props} />;
});

interface ModalHeaderProps extends NextUIModalHeaderProps { }

export const ModalHeader = forwardRef((props: ModalHeaderProps, ref) => {
  const isEmbed = useIsEmbed();
  if (isEmbed) {
    return <SimpleModalHeader className={props.className}>{props.children}</SimpleModalHeader>;
  }
  return <NextUIModalHeader ref={ref} {...props} />;
});
